import axios, { type CancelToken } from 'axios';
import { aadConfig, getAccessToken } from '../aad';
import { PRIMARY_PROVIDER_ID, type ResourceUserRole } from '../dto/account';
import type { PagedItems, Patch } from '../dto/common';
import type { ProjectContent } from '../dto/content';
import type { ProEstEstimate, ProEstEstimateDetails, ProEstTeamEx } from '../dto/proEst';
import type { Project, ProjectName } from '../dto/project';
import type { ProjectEx } from '../state';
import apiProxy from './apiProxy';

export const createProject = async (accountId: string, model: Project, l?: string): Promise<Project> => {
  await getAccessToken(aadConfig().projectScopes);

  const p = { ...model };
  if (model.office) {
    p.office = {
      id: model.office.id,
      name: model.office.name,
      isActive: model.office.isActive,
    };
  }
  if (model.provider) {
    p.provider = {
      id: model.provider.id,
      companyName: model.provider.companyName,
    };
  }
  if (model.providerOffice) {
    p.providerOffice = {
      id: model.providerOffice.id,
      name: model.providerOffice.name,
      isActive: model.providerOffice.isActive,
    };
  }

  const { data } = await axios.post(
    `${process.env.PROJECT_API}/accounts/${accountId}/projects${l ? `?l=${l}` : ''}`,
    p,
  );
  return data;
};

export const updateProject = async (
  accountId: string,
  projectId: string,
  model: Project,
  l?: string,
): Promise<Project> => {
  await getAccessToken(aadConfig().projectScopes);

  const p = { ...model };
  if (model.office) {
    p.office = {
      id: model.office.id,
      name: model.office.name,
      isActive: model.office.isActive,
    };
  }
  if (model.provider) {
    p.provider = {
      id: model.provider.id,
      companyName: model.provider.companyName,
    };
  }
  if (model.providerOffice) {
    p.providerOffice = {
      id: model.providerOffice.id,
      name: model.providerOffice.name,
      isActive: model.providerOffice.isActive,
    };
  }

  const { data } = await axios.put(
    `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}${l ? `?l=${l}` : ''}`,
    p,
  );
  return data;
};

export const patchProject = async (
  accountId: string,
  projectId: string,
  patch: Patch[],
  l?: string,
): Promise<Project> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.patch<Project>(
    `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}${l ? `?l=${l}` : ''}`,
    patch,
  );
  return data;
};

export const getProjects = apiProxy(
  'getProjects',
  async (
    accountId: string | null,
    officeId: string | null,
    offset: number,
    limit: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
    year?: number,
    month?: number,
    /**
     * Ask for the slim estimate and labour slice the assignment views need.
     * Off by default: it is roughly 140 KB that most callers never read.
     */
    assignments?: boolean,
  ): Promise<{ total: number; projects: Project[] }> => {
    await getAccessToken(aadConfig().projectScopes);

    const q: string[] = [`offset=${offset}`, `limit=${limit}`];
    if (sort) {
      q.push(`sort=${encodeURIComponent(sort)}`);
      q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
    }
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    if (officeId) q.push(`office=${officeId}`);

    if (year != null && month != null) {
      q.push(`year=${year}`);
      q.push(`month=${month}`);
    }

    if (assignments) q.push('assignments=true');

    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId || 'provider'}/projects${q.length ? `?${q.join('&')}` : ''}`,
    );
    return data;
  },
);

// New function to get account IDs where user has project access
export const getUserAccountIds = apiProxy('getUserAccountIds', async (userId?: string): Promise<string[]> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/user-account-ids${userId ? `?userId=${userId}` : ''}`);
  return data;
});

// New function to get projects across multiple accounts for experts
export const getProjectsForExpert = apiProxy(
  'getProjectsForExpert',
  async (
    officeId: string | null,
    offset: number,
    limit: number,
    sort?: string,
    sortDir?: string,
    filter?: string,
    year?: number,
    month?: number,
  ): Promise<{ total: number; projects: Project[] }> => {
    await getAccessToken(aadConfig().projectScopes);

    // First, get all account IDs where the user has project access
    const accountIds = await getUserAccountIds();

    if (accountIds.length === 0) {
      return { total: 0, projects: [] };
    }

    // Fetch projects from all accounts
    const allProjects: Project[] = [];
    let totalProjects = 0;

    for (const accountId of accountIds) {
      try {
        const q: string[] = [`offset=0`, `limit=1000`]; // Get all projects from each account
        if (sort) {
          q.push(`sort=${encodeURIComponent(sort)}`);
          q.push(`sortDir=${encodeURIComponent(sortDir || 'asc')}`);
        }
        if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

        if (officeId) q.push(`office=${officeId}`);

        if (year != null && month != null) {
          q.push(`year=${year}`);
          q.push(`month=${month}`);
        }

        const { data } = await axios.get(
          `${process.env.PROJECT_API}/accounts/${accountId}/projects${q.length ? `?${q.join('&')}` : ''}`,
        );
        allProjects.push(...data.projects);
        totalProjects += data.total;
      } catch (error) {
        console.error(`Failed to fetch projects for account ${accountId}:`, error);
      }
    }

    // Sort and paginate the combined results
    let sortedProjects = allProjects;
    if (sort) {
      sortedProjects = allProjects.sort((a, b) => {
        const aValue = a[sort as keyof Project];
        const bValue = b[sort as keyof Project];

        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        return sortDir === 'desc' ? -comparison : comparison;
      });
    }

    return {
      total: totalProjects,
      projects: sortedProjects.slice(offset, offset + limit),
    };
  },
);

export const getProjectNames = apiProxy('getProjectNames', async (accountId: string | null): Promise<ProjectName[]> => {
  await getAccessToken(aadConfig().projectScopes);

  const { data } = await axios.get(
    `${process.env.PROJECT_API}/accounts/${accountId ?? PRIMARY_PROVIDER_ID}/projects/names`,
  );
  return data;
});

export const getProject = apiProxy(
  'getProject',
  async (accountId: string, projectId: string, l?: string): Promise<ProjectEx> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}${l ? `?l=${l}` : ''}`,
    );
    return data;
  },
);

export interface ProjectBasic {
  accountId: string;
  accountName: string;
  projectId: string;
  projectName: string;
}

export const getProjectBasic = apiProxy('getProjectBasic', async (projectId: string): Promise<ProjectBasic> => {
  await getAccessToken(aadConfig().projectScopes);
  const { data } = await axios.get(`${process.env.PROJECT_API}/projects/${projectId}`);
  return data;
});

export interface UploadFileEvent {
  state: 'progress' | 'complete' | 'cancel' | 'error';
  name: string;
  progress: number;
  complete?: boolean;
  cancel?: string;
  error?: Error;
  content?: ProjectContent;
}

interface UploadBlock {
  state: 'pending' | 'upload' | 'complete';
  blockId: string;
  blob: Blob;
  size: number;
}

export const uploadFile = async (
  accountId: string,
  projectId: string,
  category: string,
  phaseId: string | null,
  file: File,
  onUploadProgress: (e: UploadFileEvent) => void,
  cancelToken?: CancelToken,
  estimateId?: string,
  metadata?: { [key: string]: string },
): Promise<void> => {
  await getAccessToken(aadConfig().projectScopes);

  // const startTime = Date.now();

  const { data: start } = await axios.post<{
    contentId: string;
    uri: string;
  }>(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
    step: 'start',
    category,
    phaseId,
    estimateId,
    fileName: file.name,
    contentType: file.type,
    size: file.size,
    metadata,
  });
  const { contentId } = start;
  const sasUri = start.uri;

  // Split the file into blocks
  const blocks = ((): UploadBlock[] => {
    const pad = (number: number, length: number) => {
      let str = `${number}`;
      while (str.length < length) str = `0${str}`;
      return str;
    };

    let remaining = file.size;
    let maxBlockSize = 2 * 1024 * 1024; // 2MB
    if (remaining < maxBlockSize) maxBlockSize = remaining;
    let offset = 0;

    let index = 1;
    const theBlocks: UploadBlock[] = [];
    while (remaining > 0) {
      theBlocks.push({
        state: 'pending',
        blockId: btoa(`block-${pad(index, 6)}`),
        blob: file.slice(offset, offset + maxBlockSize),
        size: maxBlockSize,
      });

      offset += maxBlockSize;
      remaining -= maxBlockSize;
      if (remaining <= 0) break;
      if (remaining < maxBlockSize) maxBlockSize = remaining;

      index++;
    }
    return theBlocks;
  })();

  let inCommit = false;

  const uploadAxios = axios.create();
  delete uploadAxios.defaults.headers.common.Authorization;

  // Add cleanup if the user closed the window before we complete.
  const windowUnload = () => {
    // Let the back-end know we canceled!
    axios.post(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
      step: 'cancel',
      category,
      phaseId,
      contentId,
    });
  };
  window.addEventListener('unload', windowUnload);

  const commitBlockList = () => {
    const uri = `${sasUri}&comp=blocklist`;
    const requestBody = `<?xml version="1.0" encoding="utf-8"?><BlockList>${blocks.map((block) => `<Latest>${block.blockId}</Latest>`).join('')}</BlockList>`;

    return uploadAxios.put(uri, requestBody, {
      headers: {
        'x-ms-blob-content-type': file.type,
      },
    });
  };

  const loadEventHandler = (index: number, e: ProgressEvent<FileReader>, uploadBlock: (index: number) => void) => {
    if (!e.target?.result) return;

    const a = e.target.result as ArrayBuffer;
    const requestData = new Uint8Array(a);

    blocks[index].state = 'upload';
    const uri = `${sasUri}&comp=block&blockid=${blocks[index].blockId}`;

    uploadAxios
      .put(uri, requestData, {
        headers: {
          'x-ms-blob-type': 'BlockBlob',
          'Content-Type': 'application/pdf',
        },
        cancelToken,
      })
      .then(() => {
        blocks[index].state = 'complete';

        const completeCount = blocks.filter((x) => x.state === 'complete').length;

        onUploadProgress({
          state: 'progress',
          name: file.name,
          progress: Math.min(1, completeCount / blocks.length),
        });

        // Done?
        if (completeCount >= blocks.length) {
          if (inCommit) return;

          inCommit = true;
          commitBlockList()
            .then(() =>
              axios.post<ProjectContent>(
                `${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`,
                {
                  step: 'complete',
                  category,
                  phaseId,
                  contentId,
                },
              ),
            )
            .then(({ data: content }) => {
              onUploadProgress({
                state: 'complete',
                name: file.name,
                progress: 1,
                content,
              });
              // console.log(`Total Upload Time: ${Date.now() - startTime}ms`);
            })
            .catch((err) => {
              // Let the back-end know we failed!
              axios
                .post(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
                  step: 'error',
                  category,
                  phaseId,
                  contentId,
                })
                .finally(() =>
                  onUploadProgress({
                    state: 'error',
                    name: file.name,
                    progress: 0,
                    error: err,
                  }),
                );
            })
            .finally(() => {
              // Remove the unload event handler
              window.removeEventListener('unload', windowUnload);
            });

          return;
        }

        const nextIndex = blocks.findIndex((x) => x.state === 'pending');
        if (nextIndex < 0) return;

        // Start the next block!
        uploadBlock(nextIndex);
      })
      .catch((err) => {
        // Remove the unload event handler
        window.removeEventListener('unload', windowUnload);

        // Canceled?
        if (!err.message) {
          // Let back-end know we failed!
          axios
            .post(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
              step: 'cancel',
              category,
              phaseId,
              contentId,
            })
            .finally(() => onUploadProgress({ state: 'cancel', name: file.name, progress: 0 }));
          return;
        }

        // Let back-end know we failed!
        axios
          .post(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
            step: 'error',
            category,
            phaseId,
            contentId,
          })
          .finally(() =>
            onUploadProgress({
              state: 'error',
              name: file.name,
              progress: 0,
              error: err,
            }),
          );
      });
  };

  const errorEventHandler = (e: ProgressEvent<FileReader>) => {
    if (!e.target?.error) return;

    const { error } = e.target;

    // Let back-end know we failed!
    axios
      .post(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/direct`, {
        step: 'error',
        category,
        phaseId,
        contentId,
      })
      .finally(() =>
        onUploadProgress({
          state: 'error',
          name: file.name,
          progress: 0,
          error,
        }),
      );

    // Remove the unload event handler
    window.removeEventListener('unload', windowUnload);
  };

  const uploadBlock = (index: number) => {
    const r = new FileReader();

    r.onerror = errorEventHandler;
    r.onload = (e) => loadEventHandler(index, e, uploadBlock);
    r.readAsArrayBuffer(blocks[index].blob);
  };

  // Kick max of 20!
  for (let i = 0; i < Math.min(blocks.length, 20); i++) {
    uploadBlock(i);
  }
};

export const deleteFile = async (accountId: string, projectId: string, contentId: string) => {
  await getAccessToken(aadConfig().projectScopes);
  await axios.delete(`${process.env.CONTENT_API}/accounts/${accountId}/projects/${projectId}/${contentId}`);
};

export const deletePhaseEstimate = async (accountId: string, projectId: string, phaseId: string) => {
  await getAccessToken(aadConfig().projectScopes);
  await axios.delete(
    `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/estimate`,
  );
};

export const deleteProject = async (accountId: string, projectId: string, force?: boolean): Promise<void> => {
  await getAccessToken(aadConfig().projectScopes);
  await axios.delete(
    `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}${force ? '?force=true' : ''}`,
  );
};

export const updateProjectUsers = apiProxy(
  'updateProjectUsers',
  async (accountId: string, projectId: string, users: { userId: string; role: ResourceUserRole }[]): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.post(`${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/users`, users);
  },
);

export const updateProjectUserRole = apiProxy(
  'updateProjectUserRole',
  async (accountId: string, projectId: string, userId: string, role: ResourceUserRole): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.put(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/users/${userId}?role=${role}`,
    );
  },
);

export const deleteProjectUser = apiProxy(
  'deleteProjectUser',
  async (accountId: string, projectId: string, userId: string): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.delete(`${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/users/${userId}`);
  },
);

/**
 * Opens or closes the money on a project for one outside collaborator.
 *
 * Least privilege is the default, so this is only ever the decision to open it or to close it
 * again - made by whoever runs the project, for one person, on one project. The server refuses it
 * for anybody who is not an outside collaborator: every other answer comes from which side of the
 * project somebody is on, and this must not become a second author of that.
 */
export const setTeamFeeVisibility = apiProxy(
  'setTeamFeeVisibility',
  async (accountId: string, projectId: string, userId: string, visible: boolean): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.put(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/team/${userId}/fees?visible=${visible}`,
    );
  },
);

export const updatePhaseUsers = apiProxy(
  'updatePhaseUsers',
  async (
    accountId: string,
    _projectId: string,
    phaseId: string,
    users: { userId: string; role: ResourceUserRole }[],
  ): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.post(`${process.env.PROJECT_API}/accounts/${accountId}/phases/${phaseId}/users`, users);
  },
);

export const updatePhaseUserRole = apiProxy(
  'updatePhaseUserRole',
  async (accountId: string, phaseId: string, userId: string, role: ResourceUserRole): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.put(`${process.env.PROJECT_API}/accounts/${accountId}/phases/${phaseId}/users/${userId}?role=${role}`);
  },
);

export const deletePhaseUser = apiProxy(
  'deletePhaseUser',
  async (accountId: string, phaseId: string, userId: string): Promise<void> => {
    await getAccessToken(aadConfig().projectScopes);
    await axios.delete(`${process.env.PROJECT_API}/accounts/${accountId}/phases/${phaseId}/users/${userId}`);
  },
);

export type CostDataSource = { id?: string; label?: string; year?: string; source?: string };

export type ReviewChoice = {
  value: string;
  label: string;
  /** The sentence this answer prints, or null when it prints nothing. */
  prints?: string;
};

export type ReviewItem = {
  key: string;
  label: string;
  value?: string;
  /** Needs an affirmative answer before the document can be sent. */
  loadBearing: boolean;
  answered: boolean;
  /** The value shown came from a standard default, not an answer. */
  fromDefault: boolean;
  /** Why the platform proposed what it proposed. */
  explain?: string;
  /** The exact text this puts in the document. */
  drafted?: string[];
  /** The answers available, each with the line it prints. */
  choices?: ReviewChoice[];
  /**
   * For an editable set, the baseline ids still in play, in the order they
   * appear at the front of `drafted`. A drafted line past the end of this list
   * is one the estimator added.
   */
  removableIds?: string[];
  /** The lines the estimator added to this set. */
  addedLines?: string[];
};

export type EstimateReviewGate = {
  items: ReviewItem[];
  outstanding: number;
  clear: boolean;
};

export type PhaseEstimateSetup = {
  parameters?: Record<string, unknown>;
  costDataSources?: CostDataSource[];
  grossFloorArea?: number;
  /** Read from the estimate, for the estimator to confirm. Never stored on its own. */
  candidateGrossFloorArea?: number;
  estimatingTool?: EstimatingToolSelection;
  reviewGate?: EstimateReviewGate;
  /** Entries this firm has added to its own pickers, offered on every project. */
  firmLibrary?: { estimatingTools: string[]; costDataSources: string[] };
};

/** Everything the estimate setup card shows, in one round trip. */
export const getEstimateSetup = apiProxy(
  'getEstimateSetup',
  async (accountId: string, projectId: string, phaseId: string): Promise<PhaseEstimateSetup> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/estimate-setup`,
    );
    return data;
  },
);

/** Saves only the sections passed; an absent section is left as it was. */
export const setEstimateSetup = apiProxy(
  'setEstimateSetup',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
    setup: PhaseEstimateSetup,
  ): Promise<PhaseEstimateSetup> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.put(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/estimate-setup`,
      setup,
    );
    return data;
  },
);

/** A person's edits to a drafted set: what they took out, and what they added. */
export type BoeLibraryEdits = {
  removedIds: string[];
  addedLines: string[];
};

export type EstimatingToolSelection = {
  id?: string;
  label?: string;
  version?: string;
  /** 'manual' when the estimator chose it; otherwise which default supplied it. */
  source?: string;
};

/** The tool this estimate is recorded against, or the best default. */
export const getEstimatingTool = apiProxy(
  'getEstimatingTool',
  async (accountId: string, projectId: string, phaseId: string): Promise<EstimatingToolSelection> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/estimating-tool`,
    );
    return data;
  },
);

/** Records the estimator's own choice, which no later default displaces. */
export const setEstimatingTool = apiProxy(
  'setEstimatingTool',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
    selection: EstimatingToolSelection,
  ): Promise<EstimatingToolSelection> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.put(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/estimating-tool`,
      selection,
    );
    return data;
  },
);

export const getProEst = apiProxy(
  'getProEst',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
  ): Promise<{ estimate: ProEstEstimate; details: ProEstEstimateDetails }> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/proest`,
    );
    return data;
  },
);

export const createProEst = apiProxy(
  'createProEst',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
  ): Promise<{ estimate: ProEstEstimate; details: ProEstEstimateDetails }> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.post(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/proest`,
    );
    return data;
  },
);

export const getProEstTeam = apiProxy(
  'getProEstTeam',
  async (accountId: string, projectId: string): Promise<ProEstTeamEx[]> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/proest/team`,
    );
    return data;
  },
);

export const importEstimateItems = apiProxy(
  'importEstimateItems',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
    items: any[],
    // How many rows had no CSI division at import. Optional and query-only, so
    // the request body stays exactly the item list every existing caller sends.
    unclassified?: number,
  ): Promise<{ estimate?: ProEstEstimate; details?: ProEstEstimateDetails; project?: Project }> => {
    await getAccessToken(aadConfig().projectScopes);
    const q = unclassified === undefined ? '' : `?unclassified=${unclassified}`;
    const { data } = await axios.post(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/proest/details${q}`,
      items,
    );
    return data;
  },
);

export interface PayInvoiceRequest {
  cardToken: string;
  amount: number;
}

export const payPhaseInvoice = apiProxy(
  'payPhaseInvoice',
  async (
    accountId: string,
    projectId: string,
    phaseId: string,
    request: PayInvoiceRequest,
  ): Promise<{ success: boolean; paymentId: string }> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.post(
      `${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/phases/${phaseId}/pay-invoice`,
      request,
    );
    return data;
  },
);

export const notifyProductionManagerOfFeeUpdate = async (accountId: string, projectId: string): Promise<void> => {
  await getAccessToken(aadConfig().projectScopes);
  await axios.post(`${process.env.PROJECT_API}/accounts/${accountId}/projects/${projectId}/notify-production-manager`);
};

////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// V2/FX methods!
//

export const getProjectsFx = apiProxy(
  'getProjectsFx',
  async (offset: number, limit: number, sort?: string, filter?: string): Promise<PagedItems<Project>> => {
    await getAccessToken(aadConfig().accountScopes);

    const q: string[] = [`offset=${offset}`, `limit=${limit}`];
    if (sort) q.push(`sort=${encodeURIComponent(sort)}`);
    if (filter) q.push(`filter=${encodeURIComponent(filter)}`);

    return (await axios.get(`${process.env.PROJECT_HOST}/v1/projects${q.length ? `?${q.join('&')}` : ''}`)).data;
  },
);

/** One phase of a fee, as the client is shown it. */
export interface ClientFeeProposalLine {
  phase: string;
  labor: number;
  techFee: number;
  otherDirectCosts: number;
  total: number;
}

/** A fee proposal, as the client is shown it. */
export interface ClientFeeProposal {
  providerName: string;
  clientName: string;
  projectName: string;
  projectId: string;
  expiresAt?: string;
  lines: ClientFeeProposalLine[];
  grandTotal: number;
}

/**
 * The fee proposal a client may read for one project.
 *
 * Its own endpoint rather than the project read, because the project carries the firm's working -
 * rates, per-person cost, margin - and none of that belongs to the client being asked to approve
 * it. Authorised by the grant the caller holds on the project, with no account in the path.
 */
export const getClientFeeProposal = apiProxy(
  'getClientFeeProposal',
  async (projectId: string, abort?: AbortController): Promise<ClientFeeProposal> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.get(`${process.env.PROJECT_API}/projects/${projectId}/fee-proposal`, {
      signal: abort?.signal,
    });
    return data;
  },
);

/**
 * The fee proposal as a PDF, rendered by the server.
 *
 * The provider app used to draw this itself with html2pdf over a React component, which is why the
 * only branded copy of a proposal lived in whoever's browser had drawn it and the email that sends
 * it had to attach something else - a plain table that said different things and was dated a day
 * apart. One renderer now, on the server, so the download and the client's attachment are the same
 * document.
 * @param projectId - The project.
 * @param abort - An abort controller.
 * @returns The PDF and the name the server says to save it under.
 */
export const getClientFeeProposalPdf = apiProxy(
  'getClientFeeProposalPdf',
  async (projectId: string, abort?: AbortController): Promise<{ blob: Blob; fileName: string }> => {
    await getAccessToken(aadConfig().projectScopes);
    const response = await axios.get(`${process.env.PROJECT_API}/projects/${projectId}/fee-proposal/pdf`, {
      responseType: 'blob',
      signal: abort?.signal,
    });

    // The server names the file, so both copies of one proposal are saved under the same name.
    const disposition = String(response.headers?.['content-disposition'] ?? '');
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);

    return {
      blob: response.data as Blob,
      fileName: match ? decodeURIComponent(match[1]) : `FeeProposal_${projectId.slice(0, 8).toUpperCase()}.pdf`,
    };
  },
);

/** What the client decided, and what came of it. */
export interface ClientFeeDecisionResult {
  action: string;
  status: string;
  decidedAt: string;
  decidedById: string;
}

/**
 * Records the client's decision on a fee.
 *
 * A closed set of actions and, for a counter, one amount. The server builds the change from those
 * two things, so nothing else about the project can travel with it.
 */
export const postClientFeeDecision = apiProxy(
  'postClientFeeDecision',
  async (
    projectId: string,
    action: 'approve' | 'counter' | 'decline',
    options?: { counter?: number; reason?: string },
    abort?: AbortController,
  ): Promise<ClientFeeDecisionResult> => {
    await getAccessToken(aadConfig().projectScopes);
    const { data } = await axios.post(
      `${process.env.PROJECT_API}/projects/${projectId}/fee-decision`,
      { action, counter: options?.counter, reason: options?.reason },
      { signal: abort?.signal },
    );
    return data;
  },
);
